import { toast } from "sonner";
import { Copy, LayoutGrid, Tag, Sparkles, Clock, Star, MapPin, Building2, ListChecks, Hash, Eye, CalendarDays, Ruler, Phone, Gift } from "lucide-react";
import { petCareRows } from "@/lib/petCareRows";
import { useCachedGet } from "@/lib/cache";
import { buildAdRows } from "@/components/AdDetails";
import { OfferPrice } from "@/components/listing/OfferPrice";
import { categoryName, subcategoryName, cityName, adText } from "@/lib/constants";

const ICONS = { category: LayoutGrid, subcategory: Tag, condition: Sparkles, age: Clock, rating: Star, emirate: MapPin, city: Building2, views: Eye, date: CalendarDays, adno: Hash, refno: Hash };
const fieldIcon = (type) => ({ number: Ruler, phone: Phone, hours: Clock }[type] || ListChecks);
const itemText = (x) => (x?.offer ? `${x.name}: ${x.text}` : x?.plain ? `${x.name}: ${x.plain}` : x);
export const asText = (v) => (v?.line ? v.line.filter(Boolean).join(" — ") : v?.offer ? v.text : Array.isArray(v) ? v.map(itemText).join("، ") : v?.items ? [...v.items.map(([n, p]) => (p ? `${n} ${p?.offer ? p.text : p}` : n)), ...(v.all ? [`${v.allLabel}: ${v.all?.offer ? v.all.text : v.all}`] : [])].join("، ") : v);

const PriceCell = ({ p, lang, testid }) => (p?.offer
  ? <span className="inline-flex items-center gap-2 flex-wrap justify-end"><OfferPrice o={p} lang={lang} testid={testid} />{p.until && <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap" data-testid={`${testid}-until`}>· {p.until}</span>}</span>
  : <span className="font-bold text-[#1A5C3A] shrink-0">{p}</span>);

const PKG_THEME = {
  premium: ["border-[#D4AF37]/60", "bg-gradient-to-br from-[#FFF8E6] to-[#FDF1D2] text-[#8A6A12]", "bg-[#D4AF37]"],
  regular: ["border-[#1A5C3A]/25", "bg-[#EAF4EE] text-[#1A5C3A]", "bg-[#1A5C3A]"],
  cats: ["border-[#E8A04B]/50", "bg-[#FFF3E4] text-[#9A5B12]", "bg-[#E8A04B]"],
  dogs: ["border-[#3B7BBF]/40", "bg-[#EAF2FB] text-[#1F4F82]", "bg-[#3B7BBF]"],
  other: ["border-[#1A5C3A]/25", "bg-[#EAF4EE] text-[#1A5C3A]", "bg-[#1A5C3A]"],
};

// Car wash packages / pet types: each is its own card with a header, services + prices underneath.
function PackageBox({ k, label, value, lang }) {
  const premium = value.pkg === "premium";
  const [border, head, dot] = PKG_THEME[value.pkg] || PKG_THEME.regular;
  return (
    <div className="px-4 py-3" data-testid={`detail-row-${k}`}>
      <div className={`rounded-2xl border-2 overflow-hidden ${border}`}>
        <div className={`flex items-center gap-2 px-3.5 py-2.5 font-extrabold text-[15px] lg:text-[17px] ${head}`} data-testid={`${k}-header`}>
          {premium ? <Sparkles className="w-[18px] h-[18px] shrink-0" /> : value.pkg === "regular" ? <ListChecks className="w-[18px] h-[18px] shrink-0" /> : null}{label}
        </div>
        <ul className="divide-y divide-[#EFEBE0] bg-white" data-testid={`detail-value-${k}`}>
          {value.items.map(([n, p], i) => (
            <li key={i} className="flex items-center justify-between gap-3 flex-wrap px-3.5 py-2.5 text-[15px] lg:text-[16px]" data-testid={`${k}-item-${i}`}>
              <span className="flex items-center gap-2 font-semibold text-slate-900"><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />{n}</span>
              {p && <PriceCell p={p} lang={lang} testid={`${k}-offer-${i}`} />}
            </li>
          ))}
          {value.all && (
            <li className="flex items-center justify-between gap-3 flex-wrap px-3.5 py-3 bg-[#FFF8E6] border-t-2 border-[#D4AF37]/50 font-extrabold text-[15px] lg:text-[17px] text-slate-900" data-testid={`${k}-all-price`}>
              <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37] shrink-0" />{value.allLabel}</span><PriceCell p={value.all} lang={lang} testid={`${k}-all-offer`} />
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

// Pet care "🌟 عرض حصري": price for more than one animal.
function Exclusive({ k, label, value, lang }) {
  return (
    <div className="px-4 pb-3 pt-1" data-testid={`detail-row-${k}`}>
      <div className="rounded-2xl border-2 border-[#D4AF37] bg-gradient-to-br from-[#FFF8E6] to-[#FBE7B5] p-3.5 shadow-sm">
        <div className="flex items-center gap-2 mb-1 flex-wrap"><span className="bg-[#D4AF37] text-white text-[13px] lg:text-[14px] font-extrabold px-3 py-1 rounded-full" data-testid={`${k}-badge`}>{label}</span>{value.sub && <span className="text-xs font-bold text-[#8A6A12]">{value.sub}</span>}</div>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-2 text-[15px] lg:text-[16px] font-bold text-slate-900" data-testid={`detail-value-${k}`}>
          <span>{value.line[0]}</span>{value.line[1] && <PriceCell p={value.line[1]} lang={lang} testid={`${k}-price`} />}
        </div>
      </div>
    </div>
  );
}

function Services({ k, label, value, lang }) {
  if (value.pkg) return <PackageBox k={k} label={label} value={value} lang={lang} />;
  return (
    <div className="px-4 py-3" data-testid={`detail-row-${k}`}>
      <span className="flex items-center gap-2.5 text-slate-500 text-sm mb-2"><ListChecks className="w-[18px] h-[18px] text-[#1A5C3A] shrink-0" />{label}</span>
      <ul className="space-y-2.5 ps-7" data-testid={`detail-value-${k}`}>
        {value.items.map(([n, p], i) => (
          <li key={i} className="flex items-center justify-between gap-3 flex-wrap text-[15px] lg:text-[17px]" data-testid={`${k}-item-${i}`}>
            <span className="flex items-center gap-2 font-semibold text-slate-900"><span className="w-1.5 h-1.5 rounded-full bg-[#1A5C3A] shrink-0" />{n}</span>
            {p && <PriceCell p={p} lang={lang} testid={`${k}-offer-${i}`} />}
          </li>
        ))}
      </ul>
      {value.all && (value.highlight ? (
        <div className="mt-3 ms-7 rounded-2xl border-2 border-[#D4AF37]/60 bg-gradient-to-br from-[#FFF8E6] to-[#FDF1D2] px-3.5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`${k}-all-price`}>
          <span className="flex items-center gap-2 font-extrabold text-[#8A6A12] text-[16px] lg:text-[18px]"><Gift className="w-[18px] h-[18px] shrink-0" />{value.allLabel}</span>
          <span className="text-[16px] lg:text-[18px]"><PriceCell p={value.all} lang={lang} testid={`${k}-all-offer`} /></span>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-[#EFEBE0] ps-7" data-testid={`${k}-all-price`}>
          <span className="flex items-center gap-2 font-extrabold text-slate-900"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />{value.allLabel}</span>
          <PriceCell p={value.all} lang={lang} testid={`${k}-all-offer`} />
        </div>
      ))}
    </div>
  );
}

const PRICE_RE = /(\d[\d,.]*\s*(?:درهم(?: إماراتي)?|AED)|AED\s*\d[\d,.]*)/g;
// Colours only the price amounts inside a text value green.
const Money = ({ text }) => (typeof text !== "string" ? text : text.split(PRICE_RE).map((s, i) => (i % 2 ? <span key={i} className="text-[#1A5C3A] font-bold whitespace-nowrap">{s}</span> : s)));

function Special({ k, label, value, rtl }) {
  return (
    <div className="px-4 py-3" data-testid={`detail-row-${k}`}>
      <div className="rounded-2xl border border-[#D4AF37]/50 bg-gradient-to-br from-[#FFF8E6] to-[#FDF1D2] p-3.5">
        <span className="flex items-center gap-2 font-extrabold text-[#8A6A12] text-[15px] mb-2.5"><Gift className="w-[18px] h-[18px] shrink-0" />{label}</span>
        <ul className="space-y-2" data-testid={`detail-value-${k}`}>
          {value.map((x, i) => (
            <li key={i} className="flex items-center justify-between gap-3 flex-wrap text-[15px] font-bold text-slate-900 bg-white/70 rounded-xl px-3 py-2" data-testid={`${k}-item-${i}`}>
              <span className="flex items-center gap-2 whitespace-nowrap"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />{x?.name || x}</span>
              {x?.offer ? <span className="inline-flex items-center gap-2 flex-wrap"><OfferPrice o={x} lang={rtl ? "ar" : "en"} testid={`${k}-offer-${i}`} />{x.until && <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap" data-testid={`${k}-until-${i}`}>· {x.until}</span>}</span> : x?.plain && <span className="text-[#1A5C3A] whitespace-nowrap" data-testid={`${k}-price-${i}`}>{x.plain}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function useDetailRows(listing, lang) {
  const { data: meta } = useCachedGet("/meta");
  const T = (ar, en) => (lang === "en" ? en : ar);
  if (!meta || !listing) return null;
  const sub = subcategoryName(listing.category, listing.subcategory, lang);
  const area = adText(listing, "city", lang);
  const ad = buildAdRows(meta, listing, lang);
  const at = (k) => ad.filter((r) => r[0] === k);
  return {
    main: [
      ["category", T("القسم", "Category"), categoryName(listing.category, lang)],
      ...(sub ? [["subcategory", T("النوع", "Type"), sub]] : []),
      ...at("condition"), ...at("age"), ...at("rating"),
      ["emirate", T("الإمارة", "Emirate"), cityName(listing.city, lang)],
      ...(area ? [["city", T("المدينة / القرية", "City / Village"), area]] : []),
      ...(listing.subcategory === "pet_care" ? petCareRows(meta, listing, lang) : ad.filter((r) => r[0].startsWith("f_"))),
    ],
    meta: [
      ["views", T("المشاهدات", "Views"), String(listing.views ?? 0)],
      ["date", T("تاريخ النشر", "Published"), listing.created_at ? new Date(listing.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "ar-AE-u-nu-latn") : "—"],
      ["refno", T("الرقم المرجعي", "Reference number"), listing.ref_no || "—"],
      ["adno", T("رقم الإعلان", "Ad number"), listing.ad_number || "—"],
    ],
  };
}

function Row({ k, label, value, type, rtl }) {
  const Icon = ICONS[k] || fieldIcon(type);
  return (
    <div className={`flex ${Array.isArray(value) ? "items-start" : "items-center"} justify-between gap-4 px-4 py-3`} dir={rtl ? "rtl" : "ltr"} data-testid={`detail-row-${k}`}>
      <span className="flex items-center gap-2.5 text-slate-500 text-sm min-w-0"><Icon className="w-[18px] h-[18px] text-[#1A5C3A] shrink-0" /><span dir="auto" className="leading-snug">{label}</span></span>
      {type === "dwoffer"
        ? <span className={`flex ${value.inline ? "flex-row flex-wrap items-center gap-2 justify-end" : `flex-col gap-1 ${rtl ? "items-end" : "items-start"}`}`} data-testid={`detail-value-${k}`}>
            <OfferPrice o={value} lang={rtl ? "ar" : "en"} testid={`detail-offer-${k}`} />
            {value.until && <span className="text-[11px] text-slate-500 whitespace-nowrap" data-testid={`detail-offer-until-${k}`}>{value.inline && "· "}{value.until}</span>}
          </span>
        : Array.isArray(value)
        ? <ul className="flex flex-col items-start font-semibold text-slate-900 text-sm lg:text-base space-y-1" data-testid={`detail-value-${k}`}>{value.map((x, i) => <li key={i} className="flex items-center gap-1.5 flex-wrap"><span className="w-1.5 h-1.5 rounded-full bg-[#1A5C3A] shrink-0" />{x?.offer ? <>{x.name}: <OfferPrice o={x} lang={rtl ? "ar" : "en"} testid={`detail-offer-${k}-${i}`} /></> : <Money text={x} />}</li>)}</ul>
        : <span className={`font-semibold text-sm lg:text-base ${type === "price" ? "text-[#1A5C3A] font-bold" : "text-slate-900"} ${rtl ? "text-left" : "text-right"}`} dir={type === "phone" ? "ltr" : "auto"} data-testid={`detail-value-${k}`}>{type === "price" ? value : <Money text={value} />}</span>}
    </div>
  );
}

export function DetailsTable({ listing, lang, title, price }) {
  const rows = useDetailRows(listing, lang);
  const T = (ar, en) => (lang === "en" ? en : ar);
  if (!rows) return null;
  const copy = async () => {
    const text = [title, price, ...[...rows.main, ...rows.meta].map(([, l, v]) => `${l}: ${asText(v)}`), window.location.href].join("\n");
    try { await navigator.clipboard.writeText(text); toast.success(T("تم نسخ التفاصيل", "Details copied")); } catch { toast.error(T("تعذّر النسخ", "Copy failed")); }
  };
  return (
    <section className="bg-white rounded-3xl border border-[#EFEBE0] overflow-hidden" data-testid="listing-ad-details">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#EFEBE0]">
        <h2 className="font-headline font-bold text-base">{T("تفاصيل الإعلان", "Ad details")}</h2>
        <button onClick={copy} className="flex items-center gap-1.5 text-sm font-semibold text-[#1A5C3A] bg-[#E8EFEA] px-3 h-8 rounded-full" data-testid="detail-copy-details"><Copy className="w-4 h-4" />{T("نسخ التفاصيل", "Copy details")}</button>
      </div>
      <div className="divide-y divide-[#F1EEE6]">
        {rows.main.map(([k, l, v, type]) => (type === "services" ? <Services key={k} k={k} label={l} value={v} lang={lang} /> : type === "special" ? <Special key={k} k={k} label={l} value={v} rtl={lang !== "en"} /> : type === "exclusive" ? <Exclusive key={k} k={k} label={l} value={v} lang={lang} /> : <Row key={k} k={k} label={l} value={v} type={type} rtl={lang !== "en"} />))}
      </div>
      <div className="divide-y divide-[#F1EEE6] bg-[#FAF8F3] border-t border-[#EFEBE0]">
        {rows.meta.map(([k, l, v]) => <Row key={k} k={k} label={l} value={v} rtl={lang !== "en"} />)}
      </div>
    </section>
  );
}
